import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getClubSessions } from '../../api/clubs';
import type { SessionSummary } from '../../types/api';

interface Props {
  clubId: number;
}

function statusBadge(status: string) {
  if (status === 'Active') {
    return (
      <span className="rounded-full bg-teal-100 dark:bg-teal-900/30 px-2.5 py-0.5 text-xs font-semibold text-teal-700 dark:text-teal-400">
        Active
      </span>
    );
  }
  return (
    <span className="rounded-full bg-slate-100 dark:bg-slate-700 px-2.5 py-0.5 text-xs font-semibold text-slate-500 dark:text-slate-400">
      Ended
    </span>
  );
}

export function ClubSessionsList({ clubId }: Props) {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getClubSessions(clubId)
      .then(setSessions)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load sessions'))
      .finally(() => setLoading(false));
  }, [clubId]);

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
      <h2 className="mb-4 text-base font-semibold text-slate-900 dark:text-slate-50">Recent Sessions</h2>

      {loading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700" />
          ))}
        </div>
      )}
      {error && <p className="text-sm text-red-500">{error}</p>}

      {!loading && !error && sessions.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">No sessions yet</p>
      )}

      {!loading && !error && sessions.length > 0 && (
        <ul className="divide-y divide-slate-100 dark:divide-slate-700">
          {sessions.map((session) => (
            <li key={session.id} className="flex items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{session.name}</span>
                  {statusBadge(session.status)}
                </div>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                  {session.spotName} · {new Date(session.startTime).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} · {session.participantCount} {session.participantCount === 1 ? 'angler' : 'anglers'}
                </p>
              </div>
              <Link
                to={`/sessions/${session.id}`}
                state={{ from: `/clubs/${clubId}` }}
                className="shrink-0 text-xs text-teal-600 dark:text-teal-400 hover:underline"
              >
                View
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
